import axios from 'axios';
import { toast } from 'react-toastify';
import { fireAdConversions } from './util/adConversions';
async function performRequest(requestPromise) {
  try {
    const response = await requestPromise;
    return response.data;
  } catch (error) {
    if (!error.response) {
      toast('Unable to connect. Please check your connection and try again.');
      return {
        error: 'Unable to connect'
      };
    }
    if (error.response.status === 400 && error.response.data) {
      return error.response.data;
    }
    toast('An error occurred, please try again later');
    if (error.response.status === 500) {
      return {
        error: error.response.statusText
      };
    }
    if (error.response) {
      return error.response.data;
    }
    return {
      error: 'error'
    };
  }
}
export async function postProfile(username, avatar) {
  return performRequest(axios.post('/api/user/profile', {
    username,
    avatar
  }));
}
export async function getUserFromSession(queryString, pathname) {
  // The referrer only matters on the first request, when the guest account is created.
  const referrer = typeof document !== 'undefined' ? document.referrer || undefined : undefined;
  // The time zone decides whether ad tags and ad-platform conversions are allowed (none for EU/UK).
  let tz;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (_) {
    tz = undefined;
  }
  const params = {
    queryString,
    pathname,
    referrer,
    tz
  };
  const response = await performRequest(axios.get('/api/user/from-session', {
    params
  }));
  // Conversions the server recorded (sign-up, purchase) fire in the browser tags with the same event ids.
  fireAdConversions(response?.adConversions);
  return response;
}

// Browser ad conversions the server has queued for this user (src/util/adConversions.js).
export async function getAdConversions() {
  return performRequest(axios.get('/api/user/ad-conversions'));
}
export async function postLobby(options = {}) {
  return performRequest(axios.post('/api/lobby', options));
}
export async function postLobbyGame(lobbyId, options = {}) {
  return performRequest(axios.post(`/api/lobby/${lobbyId}/game`, options));
}
export async function postPlaylistNext(lobbyId, from) {
  return performRequest(axios.post(`/api/lobby/${lobbyId}/playlist/next`, {
    from
  }));
}

// TEAMBRIDGE_QUICK_LINEUP: the room's "25-minute team session" (util/quickLineup.js).
export async function postQuickLineup(lobbyId) {
  return performRequest(axios.post(`/api/lobby/${lobbyId}/quick-lineup`, {}));
}
export async function postPlaylistChange(lobbyId, change) {
  return performRequest(axios.post(`/api/lobby/${lobbyId}/playlist`, change));
}
export async function postLobbyPractice(lobbyId) {
  return performRequest(axios.post(`/api/lobby/${lobbyId}/practice`));
}
export async function postLobbyGameEnd(lobbyId, options = {}) {
  return performRequest(axios.post(`/api/lobby/${lobbyId}/game/end`, options));
}
export async function postLobbyBugReport(lobbyId, options = {}) {
  return performRequest(axios.post(`/api/lobby/${lobbyId}/game/bug`, options));
}
export async function getActiveLobbies() {
  return performRequest(axios.get('/api/lobby/active'));
}
export async function postContactForm(body) {
  return performRequest(axios.post('api/contactform', body));
}
export async function postNotificationViewed(notificationId) {
  return performRequest(axios.post(`/api/notification/${notificationId}/viewed`));
}
export async function kickUserFromLobby(id, userId) {
  return performRequest(axios.post(`/api/lobby/${id}/kick`, {
    userId
  }));
}
export async function makeUserLobbyHost(id, userId) {
  return performRequest(axios.post(`/api/lobby/${id}/set-host`, {
    userId
  }));
}
export async function postClaimHost(id) {
  return performRequest(axios.post(`/api/lobby/${id}/claim-host`));
}

// TEAMBRIDGE_CREDITS_OUT_OFFER: { players, games } the people in this lobby played together today, or null.
// Quiet on failure (no toast): the sheet then keeps today's words.
export async function getLobbyTeamToday(id) {
  try {
    const response = await fetch(`/api/lobby/${encodeURIComponent(id)}/team-today`, {
      credentials: 'same-origin'
    });
    return response.ok ? await response.json() : null;
  } catch (_) {
    return null;
  }
}
export async function postClaimHostWithPass(id) {
  return performRequest(axios.post(`/api/lobby/${id}/claim-host-with-pass`));
}
export async function postUserPreferences(data) {
  return performRequest(axios.post('/api/user/preferences', data));
}
export async function getTeam(hash) {
  if (hash) {
    return performRequest(axios.get(`/api/team/${hash}`));
  }
  return performRequest(axios.get('/api/team'));
}
export async function removeMemberFromTeam(teamId, userId) {
  return performRequest(axios.post(`/api/team/${teamId}/remove-member/${userId}`));
}
export async function setTeamManager(teamId, userId, enabled) {
  const action = enabled ? 'grant-manager' : 'revoke-manager';
  return performRequest(axios.post(`/api/team/${teamId}/${action}/${userId}`, {}));
}
export async function addMemberToTeam(teamId, userId) {
  return performRequest(axios.post(`/api/team/${teamId}/add-member/${userId}`));
}
export async function getLobbyByHash(hash) {
  return performRequest(axios.get(`/api/lobby/by-hash/${hash}`));
}
export async function login(email, password) {
  return performRequest(axios.post('/api/auth/login', {
    email,
    password
  }));
}
export async function postLogout() {
  return performRequest(axios.post('/api/auth/logout'));
}

// The register response carries the sign-up conversion: it fires before this resolves
// (at most ~2 s), so it's sent before the caller redirects, often straight to Stripe.
export async function register(email, password) {
  const response = await performRequest(axios.post('/api/auth/register', {
    email,
    password
  }));
  await fireAdConversions(response?.adConversions);
  return response;
}
export async function forgotPassword(email) {
  return performRequest(axios.post('/api/auth/forgot-password', {
    email
  }));
}
export async function resetPassword(resetToken, newPassword) {
  return performRequest(axios.post('/api/auth/reset-password', {
    resetToken,
    newPassword
  }));
}

// TEAMBRIDGE_GROUP_PITCH (lib/group-pitch.js): claim the end screen's Year Pass pitch, then 'clicked' or 'dismissed'.
export async function postGroupPitch(gameId, action) {
  try {
    return (await axios.post('/api/user/group-pitch', {
      gameId,
      action
    })).data;
  } catch (_) {
    return null;
  }
}
export async function trackEvent(eventName, data) {
  try {
    const res = await axios.put(`/api/analytics/event/${eventName}`, data);
    return res;
  } catch (error) {
    return null;
  }
}
export async function postTeamOnboarding(teamId, data) {
  return performRequest(axios.post(`/api/team/${teamId}/onboarding`, data));
}
export async function subscribeToNewsletter(email) {
  return performRequest(axios.post('/api/user/subscribe-to-news-letter', {
    email
  }));
}